<template>
  <div class="column gap-4">
    <template v-if="!sceneNode || !treeNode">
      <div class="message-info">
        {{ $t("messages.elementNotInScene") }}
        <i18n-t
          v-if="sceneIds.length > 0"
          keypath="messages.isPlacedInScenes"
        >
          <template #scenes>
            <LocalizedEnumeration :items="sceneIds.map(String)" v-slot="{ item }">
              <a href="#" @click="switchActiveScene(Number(item))"><strong>{{ sceneStore.titles[item] }}</strong></a>
            </LocalizedEnumeration>
          </template>
        </i18n-t>
      </div>

      <button class="button button-clear" @click="instantiateElementNode()">
        <lucide-icon name="circle-plus" class="mr-2" />
        {{ $t("actions.placeElementInScene") }}
      </button>
    </template>
    <template v-else>
      <input-switch
        :value="treeNode.visible"
        @update:value="updateSceneNodeKey('visible', $event)"
      >
        {{ $t("labels.visible") }}
      </input-switch>

      <template v-if="treeNode.displayPosition === 'scene'">
        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.valueStyle") }}</legend>
          <div class="column gap-2">
            <input-switch
              :value="sceneNode.data.valueVisible"
              @update:value="updateSceneNodeDataKey('valueVisible', $event)"
            >
              {{ $t("labels.elementValueVisible") }}
            </input-switch>
            <input-switch
              :value="sceneNode.data.barVisible"
              @update:value="updateSceneNodeDataKey('barVisible', $event)"
            >
              {{ $t("labels.elementBarVisible") }}
            </input-switch>
          </div>
          <input-switch
            v-if="sceneNode.data.barVisible"
            :value="sceneNode.data.barWidthLimited"
            @update:value="updateSceneNodeDataKey('barWidthLimited', $event)"
          >
            {{ $t("labels.barWidthLimited") }}
          </input-switch>
        </fieldset>

        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.color") }}</legend>
          <div class="line">
            <input-color-background
              :value="sceneNode.fill ?? null"
              @update:value="updateSceneNodeKey('fill', $event)"
            />
            <input-color-text
              :value="sceneNode.textColor ?? null"
              :default-value="contrastTextColor"
              @update:value="updateSceneNodeKey('textColor', $event)"
            />
          </div>
        </fieldset>

        <fieldset class="column menu-fieldset">
          <legend class="label mb-0">{{ $t("labels.title") }}</legend>

          <!-- hide title -->
          <input-switch
            :value="sceneNode.data.headlineHidden"
            @update:value="setHeadlineHidden($event)"
          >
            {{ $t("labels.hideElementTitle") }}
          </input-switch>

          <template v-if="!sceneNode.data.headlineHidden">

            <div class="input-sidelabel" :title="$t('labels.fontSize')">
              <label for="input-scene-element-font-size">
                <lucide-icon name="a-large-small" />
              </label>
              <input-number
                id="input-scene-element-font-size"
                :value="fontSize ?? sceneFontSize"
                @update:value="fontSize = $event"
              >
                <template #buttons>
                  <button
                    v-if="fontSize != sceneFontSize"
                    type="button"
                    class="button-icon input__icon"
                    @click="fontSize = null"
                    :title="$t('actions.reset')"
                  >
                    <lucide-icon name="rotate-ccw" />
                  </button>
                </template>
              </input-number>
            </div>
            <section>
              <h4 class="label mt-0 mb-2">{{ $t("labels.textAlignment") }}</h4>
              <div class="line">
                <button
                  class="button-icon -bordered"
                  :class="{ '-selected': sceneNode.textAlign == 'left' }"
                  type="button"
                  @click="updateSceneNodeKey('textAlign', 'left')"
                >
                  <AlignLeftIcon :size="18" />
                </button>
                <button
                  class="button-icon -bordered"
                  :class="{
                    '-selected': sceneNode.textAlign == 'center'
                  }"
                  type="button"
                  @click="updateSceneNodeKey('textAlign', 'center')"
                >
                  <AlignCenterIcon :size="18" />
                </button>
                <button
                  class="button-icon -bordered"
                  :class="{
                    '-selected': sceneNode.textAlign == 'right'
                  }"
                  type="button"
                  @click="updateSceneNodeKey('textAlign', 'right')"
                >
                  <AlignRightIcon :size="18" />
                </button>
              </div>
            </section>
          </template>
        </fieldset>

        <!-- image -->
        <input-image
          :id="sceneNode.data?.imageId ?? undefined"
          @update="updateImage"
          @delete="deleteImage"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T extends 'target' | 'resource' | 'index'">
import { computed } from "vue";
import { notNull } from "@/helpers/utility/Type";
import { useContrastColor, useResolvedColor } from "@/helpers/utility/Color";
import { isSceneElementNode } from "../sceneElement/sceneElementFunctions";
import eventHub from "@/helpers/utility/Events";

import { useSceneStore } from "../scene/scenes.store";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneNodesStore } from "../scene/sceneNodes.store";
import { useSceneElementStore } from "../sceneElement/sceneElements.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";

import InputSwitch from "@core/input/InputSwitch.vue";
import InputImage from "@editor/items/image/InputImage.vue";
import InputColorBackground from "@core/input/InputColorBackground.vue";
import InputColorText from "@core/input/InputColorText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import {
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon
} from "@lucide/vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import LocalizedEnumeration from "@core/wrappers/LocalizedEnumeration.vue";

import type { Uid } from "@/helpers/utility/Id";
import type { SceneNodeItem } from "../scene/SceneNodeItem";
import { usePresenceStore } from "@editor/store/presence.store";

const props = defineProps<{
  elementUid: Uid<T>;
}>();

const presence = usePresenceStore();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();
const sceneElementStore = useSceneElementStore();
const sceneStore = useSceneStore();

const treeNode = computed(() => sceneTree.elementNodes.get(props.elementUid));
const sceneNode = computed(() => treeNode.value?.node ?? null);

const nodes = computed(() => {
  return sceneNodeStore.orderedItems
    .filter(isSceneElementNode)
    .filter((node) => node.data.elementUid === props.elementUid);
});
const scenes = computed(() => {
  return nodes.value
    .map((node) => sceneStore.items[node.sceneId])
    .filter(notNull);
});
const sceneIds = computed(() => scenes.value.map((s) => s.id));

function switchActiveScene(sceneId: number) {
  presence.set("activeSceneId", sceneId);
}

async function instantiateElementNode() {
  const freeElementPosition = sceneElementStore.freeElementPosition;
  if (freeElementPosition == null) {
    console.error("No free position could be found.");
    return;
  }
  const node = await sceneElementStore.create(
    {
      x: freeElementPosition.x,
      y: freeElementPosition.y,
      width: 200,
      height: 90
    },
    {
      elementUid: props.elementUid
    }
  );
  useSelectionStore().select(node);
}

// =====================
// COLOR

const backgroundColor = useResolvedColor(
  () => sceneNode.value?.fill || "var(--color-background-deep)"
);
const contrastTextColor = useContrastColor(backgroundColor);

// =====================
// IMAGE

function updateImage(image: { width: number; height: number; id: number }) {
  console.log("update image", image.width, image.height);

  return updateSceneNodeDataKey("imageId", image.id);
}

function deleteImage() {
  updateSceneNodeDataKey("imageId", null);

  if (!sceneNode.value) return;

  // TODO.
  // Instead of updating here, trigger a recalculation in the scene nodes?
  eventHub.emit("scene-node-refresh-height", sceneNode.value.uid);
  // if (sceneNode.value) {
  //   useUndo().ignoreNextAction(true);
  //   sceneNodeStore.updateKey(sceneNode.value.id, "height", targetItem.minHeight);
  // }
}

// =====================
// HEADLINE

function setHeadlineHidden(hidden: boolean) {
  updateSceneNodeDataKey("headlineHidden", hidden);

  if (!sceneNode.value) return;

  // TODO:
  eventHub.emit("scene-node-refresh-height", sceneNode.value.uid);
  // if (hidden) {
  //   useUndo().ignoreNextAction(true);

  //   ctx.updateKey("height", targetItem.minHeight);
  // }
}

// ======================
// FONT SIZE

const sceneFontSize = computed(() => sceneTree.activeScene.fontSize);

const fontSize = computed({
  get(): number | null {
    if (sceneNode.value?.fontSizeRelative != null) {
      return sceneFontSize.value * sceneNode.value.fontSizeRelative;
    }
    return sceneFontSize.value;
  },
  set(value: number | null) {
    if (!sceneNode.value) return;
    const relativeSize = value != null ? value / sceneFontSize.value : 1;
    updateSceneNodeKey("fontSizeRelative", relativeSize);
  }
});

// =====================
// UTILS

function updateSceneNodeKey<K extends keyof SceneNodeItem<"element">>(
  key: K,
  value: SceneNodeItem<"element">[K]
) {
  if (!sceneNode.value) return;
  sceneNodeStore.updateKey(sceneNode.value.id, key, value);
}

function updateSceneNodeDataKey<
  K extends keyof SceneNodeItem<"element">["data"]
>(key: K, value: SceneNodeItem<"element">["data"][K]) {
  if (!sceneNode.value) return;
  sceneNodeStore.updateDataKey<"element">(sceneNode.value.id, key, value);
}
</script>
